import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardOutlinedIcon from '@mui/icons-material/KeyboardOutlined';
import { Box, Collapse, Stack, useMediaQuery, useTheme } from '@mui/material';
import { useRef, useState, type MouseEvent } from 'react';
import type { TwoStepMethodKind } from '../../../../../@api-client';
import { FONT_MONO, RADIUS } from '../../../../../../design-system/tokens/tokens.generated';
import { QrCodeOutput } from '../../../../media/qr-code-output/qr-code-output';
import { Button } from '../../../../primitives/button';
import { Typography } from '../../../../primitives/typography/typography';
import { TwoStepCodeForm, TwoStepPhoneForm } from './two-step-forms';
import { useCopy } from './use-copy';
import type { TwoStepActions, TwoStepFeedback, TwoStepSetupStep } from './two-step.types';

const CHOICE_LABELS: Record<TwoStepMethodKind, string> = {
  authenticator: 'Use an Authenticator App',
  sms: 'Text Me a Code',
};

const keyGroups = (secret: string) => secret.match(/.{1,4}/g) ?? [secret];

const setupBody = ({ method, askingPhone }: TwoStepSetupStep) => {
  if (method?.kind === 'authenticator') {
    return 'Scan this with an authenticator app, such as Google Authenticator or Microsoft Authenticator, then enter the 6-digit code it shows.';
  }
  if (method?.kind === 'sms') return `Enter the code we texted to ${method.hint ?? 'your phone'}.`;
  if (askingPhone) return 'Enter the mobile number to text your codes to.';
  return 'Your account signs in with a second step. Choose how you want to get your codes. You can add more ways later.';
};

const COPY_LABELS = { idle: 'Copy Key', copied: 'Copied', failed: "Couldn't Copy" } as const;

const SIDE_BY_SIDE = '@container (min-width: 480px)';

// The code field below keeps focus, so these clicks neither interrupt typing nor flag it as left empty.
const keepCodeFieldFocus = (event: MouseEvent) => event.preventDefault();

const AuthenticatorKey = ({ secret, uri }: { secret: string; uri: string }) => {
  const { copyState, copy } = useCopy(secret);
  const theme = useTheme();
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const duration = reduceMotion ? 0 : theme.transitions.duration.standard;
  const qrRef = useRef<HTMLDivElement>(null);
  const [openHeight, setOpenHeight] = useState<number>();
  const showingKey = openHeight !== undefined;

  return (
    // Laid out by the width it is given, not the screen's: a dialog is narrow on a wide screen.
    <Box sx={{ containerType: 'inline-size' }}>
      <Stack
        sx={{
          gap: 1,
          alignItems: 'center',
          [SIDE_BY_SIDE]: { flexDirection: 'row', alignItems: 'flex-start' },
        }}
      >
        <Box ref={qrRef} sx={{ flex: 'none' }}>
          <QrCodeOutput value={uri} size={160} showLogo={false} />
        </Box>
        <Stack
          sx={{
            flex: 1,
            alignSelf: 'stretch',
            minWidth: 0,
            gap: 0.5,
            alignItems: 'flex-start',
            p: 1.5,
            bgcolor: 'grey.100',
            borderRadius: `${RADIUS.base}px`,
            boxSizing: 'border-box',
            transition: theme.transitions.create('min-height', { duration }),
            [SIDE_BY_SIDE]: { alignSelf: 'flex-start', minHeight: openHeight ?? 0 },
          }}
        >
          <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
            <KeyboardOutlinedIcon fontSize="small" />
            <Typography.BodyEmphasised>Can&apos;t scan the code?</Typography.BodyEmphasised>
          </Stack>
          <Typography.Subtitle>
            Enter the setup key manually in your authenticator app, as a Time based key.
          </Typography.Subtitle>
          <Stack sx={{ flex: 1, alignSelf: 'stretch', justifyContent: 'center' }}>
            <Collapse in={showingKey} timeout={duration}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, max-content)',
                  justifyContent: 'center',
                  columnGap: 2,
                  rowGap: 0.5,
                  // Offsets the text button's own top padding, so the key sits centred between the copy and its label.
                  pt: 1.75,
                  pb: 0.25,
                  userSelect: 'all',
                }}
              >
                {keyGroups(secret).map((group, index) => (
                  <Typography.BodyEmphasised key={index} sx={{ fontFamily: FONT_MONO }}>
                    {group}
                  </Typography.BodyEmphasised>
                ))}
              </Box>
            </Collapse>
          </Stack>
          <Box sx={{ mb: -1.5 }}>
            {showingKey ? (
              <Button
                variant="text"
                onMouseDown={keepCodeFieldFocus}
                onClick={copy}
                sx={{ ml: -1.25 }}
                startIcon={
                  copyState === 'copied' ? (
                    <CheckIcon fontSize="small" />
                  ) : (
                    <ContentCopyIcon style={{ fontSize: 16 }} />
                  )
                }
              >
                {COPY_LABELS[copyState]}
              </Button>
            ) : (
              <Button
                variant="text"
                onMouseDown={keepCodeFieldFocus}
                onClick={() => setOpenHeight(qrRef.current?.offsetHeight ?? 0)}
                sx={{ ml: -1.75 }}
              >
                Show Setup Key
              </Button>
            )}
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
};

type TwoStepSetupProps = TwoStepFeedback &
  Pick<
    TwoStepActions,
    'chooseMethod' | 'askForPhone' | 'submitPhone' | 'confirmMethod' | 'chooseAnotherSetupMethod'
  > & { step: TwoStepSetupStep };

export const TwoStepSetup = ({
  step,
  busy,
  error,
  chooseMethod,
  askForPhone,
  submitPhone,
  confirmMethod,
  chooseAnotherSetupMethod,
}: TwoStepSetupProps) => {
  const { method, askingPhone, kinds } = step;
  const isChoosing = !method && !askingPhone;
  const [chosen, setChosen] = useState<TwoStepMethodKind>();

  const choose = (kind: TwoStepMethodKind) => {
    setChosen(kind);
    if (kind === 'sms') askForPhone();
    else chooseMethod(kind);
  };

  return (
    <>
      <Typography.Body sx={{ pb: 1 }}>{setupBody(step)}</Typography.Body>
      {method?.kind === 'authenticator' && (
        <AuthenticatorKey secret={method.secret} uri={method.uri} />
      )}
      {method && (
        <TwoStepCodeForm
          key={method.kind}
          submitLabel="Confirm"
          busy={busy}
          error={error}
          onSubmit={confirmMethod}
        />
      )}
      {askingPhone && !method && (
        <TwoStepPhoneForm
          defaultPhoneNumber={step.phoneOnRecord}
          busy={busy}
          error={error}
          onSubmit={submitPhone}
        />
      )}
      {isChoosing && (
        <>
          {kinds.map((kind) => (
            <Button
              key={kind}
              variant={kind === kinds[0] ? 'primary' : 'secondary'}
              size="large"
              fullWidth
              disabled={busy}
              loading={busy && chosen === kind}
              loadingPosition="start"
              onClick={() => choose(kind)}
            >
              {CHOICE_LABELS[kind]}
            </Button>
          ))}
          {kinds.includes('authenticator') && (
            <Typography.Caption>
              An authenticator app is the most secure, and works without signal.
            </Typography.Caption>
          )}
        </>
      )}
      {!isChoosing && (
        <Button variant="text" disabled={busy} onClick={chooseAnotherSetupMethod}>
          Choose Another Way
        </Button>
      )}
    </>
  );
};
